import { useEffect, useMemo, useState } from "react"
import Modal from "Components/molecule/modal"
import LoadingState from "Components/organisms/loading-state"
import { GrossMarginValueClick } from "Components/templates/gross-margin-report"
import { GROSS_MARGIN_SOURCES, PAGE_CALCULATED_FIELDS } from "data/gross-margin-sources"
import { IGrossMarginReportQuery } from "models/gross-margin-report"
import { getGrossMarginBreakdown, GrossMarginBreakdownRow } from "slices/finance/gross-margin-report/thunk"
import { formatCurrency } from "utils/tools"
import { stripDepartmentPrefix } from "utils/department-options"

interface GrossMarginValueModalProps {
    click: GrossMarginValueClick | null
    query: IGrossMarginReportQuery
    onClose: () => void
}

const KIND_LABEL = { actual: "Actual", budget: "Budget", variance: "Variance $", variancePct: "Variance %" }

// Explains one value of the Gross Margin report: where it comes from and, for stored amounts,
// the department x branch rows that add up to it.
const GrossMarginValueModal = ({ click, query, onClose }: GrossMarginValueModalProps) => {
    const [rows, setRows] = useState<GrossMarginBreakdownRow[] | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    const source = click ? GROSS_MARGIN_SOURCES[click.field] : undefined
    const canBreakDown = !!click
        && (click.kind === 'actual' || click.kind === 'budget')
        && !PAGE_CALCULATED_FIELDS.has(click.field)
    const storedField = click ? `${click.field}${click.kind === 'budget' ? 'Budget' : 'Actual'}` : ''

    useEffect(() => {
        setRows(null)
        if (!click || !canBreakDown) return
        setIsLoading(true)
        getGrossMarginBreakdown(query, storedField).then(data => {
            setRows(data ?? [])
            setIsLoading(false)
        })
    }, [click])

    const total = useMemo(() => (rows ?? []).reduce((sum, r) => sum + r.amount, 0), [rows])

    if (!click) return null

    const valueText = click.value == null
        ? '-'
        : click.isPercent ? `${(click.value * 100).toFixed(2)}%` : formatCurrency(click.value)

    return (
        <Modal
            isOpen={!!click}
            onClose={onClose}
            title={`${click.label} — ${KIND_LABEL[click.kind]}`}
            subtitle={valueText}
            className="fit-content"
        >
            <div style={{ minWidth: 420, maxWidth: 720 }}>
                <h6 className="mb-1">Where it comes from</h6>
                {click.kind === 'variance' && <p className="mb-2">Actual − Budget.</p>}
                {click.kind === 'variancePct' && <p className="mb-2">(Actual − Budget) ÷ |Budget|.</p>}
                {source && <p className="mb-1">{source.source}</p>}
                {source?.formula && <p className="mb-2 text-muted">{source.formula}</p>}

                {canBreakDown && <>
                    <h6 className="mt-3 mb-2">By department and branch</h6>
                    {isLoading ? <LoadingState title="Loading" subtitle="Fetching the rows behind this value…" /> : (
                        rows && rows.length > 0 ? (
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr><th>Department</th><th>Branch</th><th className="text-end">Amount</th></tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={`${r.departmentId}-${r.branchId}`}>
                                            <td>{stripDepartmentPrefix(r.department)}</td>
                                            <td>{r.branch}</td>
                                            <td className="text-end">{formatCurrency(r.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="fw-bold"><td colSpan={2}>Total</td><td className="text-end">{formatCurrency(total)}</td></tr>
                                </tfoot>
                            </table>
                        ) : <p className="text-muted mb-0">No amounts for the selected filters.</p>
                    )}
                </>}
            </div>
        </Modal>
    )
}

export default GrossMarginValueModal
